import axios from 'axios';
import { BASE_URL, URLS } from '../constants';
import { toast } from 'react-fox-toast';
import { logSessionApiActivity } from '../utils/logger';

const apiClient = axios.create({
    baseURL: BASE_URL,
    withCredentials: true,
});

// Flag to avoid multiple refresh calls
let isRefreshing = false;
let failedQueue: any[] = [];

const processQueue = (error: any, token: string | null = null): void => {
    failedQueue.forEach((prom) => {
        if (token) {
            prom.resolve(token);
        } else {
            prom.reject(error);
        }
    });
    failedQueue = [];
};

const refreshToken = async (): Promise<string> => {
    const tokenValue = JSON.parse(sessionStorage?.access_token || '{}');
    const currentToken = tokenValue?.refreshToken;

    try {
        const response = await axios.post(
            `${BASE_URL}${URLS.refreshToken}`,
            {
                data: {},
            },
            {
                headers: {
                    Authorization: `Bearer ${currentToken}`,
                    'Content-Type': 'application/json',
                },
                withCredentials: true,
            }
        );

        const newToken = response.data?.data;

        if (!newToken) {
            throw new Error('No token received in refresh response');
        }

        sessionStorage.setItem(
            'access_token',
            JSON.stringify({ ...tokenValue, ...newToken })
        );
        return newToken?.token;
    } catch (err) {
        console.error('Token refresh failed:', err);
        throw err;
    }
};

apiClient.interceptors.request.use(
    (config) => {
        const access_token = JSON.parse(
            sessionStorage?.access_token || '{}'
        )?.token;
        if (access_token) {
            config.headers['Authorization'] = 'Bearer ' + access_token;
        }
        if (config.url?.includes('session')) {
            logSessionApiActivity({
                message: `[API Request] ${config.method?.toUpperCase()} ${config.url}`,
                config: {
                    url: config.url,
                    method: config.method,
                    headers: config.headers,
                    params: config.params,
                    data: config.data,
                },
                time: new Date().toISOString(),
            });
        }
        return config;
    },
    (error) => Promise.reject(error)
);

apiClient.interceptors.response.use(
    (response) => {
        if (response.config.url?.includes('session')) {
            logSessionApiActivity({
                message: `[API Response] ${response.config.url}`,
                config: {
                    status: response.status,
                    headers: response.headers,
                    data: response.data,
                },
                time: new Date().toISOString(),
            });
        }
        return response;
    },
    async (error) => {
        const originalRequest = error.config;
        if (originalRequest?.url?.includes('session')) {
            logSessionApiActivity({
                message: `[API Error] ${originalRequest.url}`,
                config: {
                    method: originalRequest.method,
                    data: originalRequest.data,
                    headers: originalRequest.headers,
                    status: error?.response?.status,
                    error: error?.response?.data || error.message,
                },
                time: new Date().toISOString(),
                trace: error?.stack,
            });
        }

        if (error?.response?.status === 401 && !originalRequest._retry) {
            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    failedQueue.push({
                        resolve: (token: string) => {
                            originalRequest.headers['Authorization'] =
                                'Bearer ' + token;
                            resolve(apiClient(originalRequest));
                        },
                        reject: (err: any) => reject(err),
                    });
                });
            }

            originalRequest._retry = true;
            isRefreshing = true;

            try {
                const newToken = await refreshToken();
                processQueue(null, newToken);
                originalRequest.headers['Authorization'] = 'Bearer ' + newToken;
                return apiClient(originalRequest);
            } catch (err) {
                processQueue(err, null);
                sessionStorage.removeItem('access_token');
                sessionStorage.setItem(
                    'prevState',
                    window.btoa(window.location.href)
                );
                window.location.href =
                    window.location.href.split('emr-ui')[0] + 'emr-ui/';
                return Promise.reject(err);
            } finally {
                isRefreshing = false;
            }
        }

        console.error(error);
        toast.error('Some error occurred. Please try again later.');
        return Promise.reject(error);
    }
);
export const getAuth = (url: string): Promise<any> => {
    return apiClient.get(url);
};
export const postAuth = (url: string, data: any): Promise<any> => {
    return apiClient.post(url, data);
};
export const putAuth = (url: string, data: any): Promise<any> => {
    return apiClient.put(url, data);
};
export const deleteAuth = (url: string, data?: any): Promise<any> => {
    const requestOptions: any = {};
    if (data) {
        requestOptions.data = { ...data };
    }
    return apiClient.delete(url, requestOptions);
};
export default apiClient;
